import React, { useState, useEffect } from "react";
import axios from "../axios";

/**
 * 目撃投稿の編集フォーム。**ダッシュボード（SightingModal）と過去データ
 * （ArchiveModal）で同じものを使う。**2 つに写すと、片方だけ直したときに
 * 送る中身が食い違う。
 *
 * super_admin は `PUT /api/admin/posts/{id}`（日付の制限なし・車号も送る）、
 * 投稿者本人は `PUT /api/sightings/{id}`（8 日より前は本体が弾く）。
 *
 * started_at はセルの日付（date）＋ 入力した時刻で組む。セルの日付は
 * 投稿の started_at の暦日なので、深夜便も元の日のまま直る。
 */

export const IconPencil = ({ size = 13 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

// font-size: 16px 必須（iOS auto-zoom 防止）
export const inputBase = {
  width: "100%",
  fontSize: "16px",
  padding: "7px 12px",
  borderRadius: "10px",
  border: "1.5px solid #6ee7b7",
  boxSizing: "border-box",
  background: "#fff",
  color: "#1a202c",
  fontFamily: "inherit",
  outline: "none",
  WebkitAppearance: "none",
  appearance: "none",
  boxShadow: "0 1px 3px rgba(4,120,87,0.08)",
};
export const selectBase = {
  ...inputBase,
  appearance: "none",
  WebkitAppearance: "none",
  backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24'%3E%3Cpath fill='%23059669' d='M7 10l5 5 5-5z'/%3E%3C/svg%3E\")",
  backgroundRepeat: "no-repeat",
  backgroundPosition: "right 13px center",
  paddingRight: "36px",
};

function SightingEditForm({ record, date, busNumber, routes, isSuperAdmin, onSaved, onCancel, onError }) {
  const [form, setForm] = useState({
    routeId: String(record.route_id || ""),
    startId: String(record.start_id || ""),
    time: (record.time || "").slice(0, 5),
    comment: record.comment || "",
  });
  const [starts, setStarts] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let alive = true;
    if (!form.routeId) {
      setStarts([]);
      return undefined;
    }
    axios
      .get(`/starts?route_id=${form.routeId}`)
      .then((res) => alive && setStarts(res.data))
      .catch((err) => console.error("始発地取得失敗:", err));
    return () => { alive = false; };
  }, [form.routeId]);

  const submit = async () => {
    if (!form.routeId || !form.startId || !form.time) {
      onError?.("路線・始発地・時刻は必須です。", "warning");
      return;
    }
    try {
      setLoading(true);
      const endpoint = isSuperAdmin ? `/admin/posts/${record.postId}` : `/sightings/${record.postId}`;
      const payload = {
        route_id: parseInt(form.routeId, 10),
        start_id: parseInt(form.startId, 10),
        started_at: `${date} ${form.time}:00`,
        comment: form.comment || null,
      };
      if (isSuperAdmin) payload.bus_number = busNumber;
      await axios.put(endpoint, payload);
      onSaved?.("投稿を更新しました");
    } catch (err) {
      onError?.(err.response?.data?.message || "更新に失敗しました。");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      padding: "13px",
      display: "flex", flexDirection: "column", gap: "8px",
      background: "#f8fffe",
    }}>
      <div style={{
        fontSize: "11px", fontWeight: "700", color: "#059669",
        display: "flex", alignItems: "center", gap: "5px",
        letterSpacing: "0.5px", textTransform: "uppercase",
      }}>
        <IconPencil size={11} /> 編集中
      </div>
      <select
        className="sm-input"
        aria-label="路線"
        value={form.routeId}
        onChange={(e) => setForm((f) => ({ ...f, routeId: e.target.value, startId: "" }))}
        style={selectBase}
      >
        <option value="">路線を選択</option>
        {routes.map((r) => (
          <option key={r.id} value={String(r.id)}>[{r.short_name}] {r.full_name}</option>
        ))}
      </select>
      <div style={{ display: "flex", gap: "8px" }}>
        <select
          className="sm-input"
          aria-label="始発バス停"
          value={form.startId}
          onChange={(e) => setForm((f) => ({ ...f, startId: e.target.value }))}
          disabled={!form.routeId}
          style={{ ...selectBase, flex: "1 1 0", minWidth: 0, width: "auto", opacity: form.routeId ? 1 : 0.45 }}
        >
          <option value="">始発バス停を選択</option>
          {starts.map((st) => (
            <option key={st.id} value={String(st.id)}>[{st.short_name}] {st.full_name}</option>
          ))}
        </select>
        <input
          className="sm-input"
          type="time"
          aria-label="時刻"
          value={form.time}
          onChange={(e) => setForm((f) => ({ ...f, time: e.target.value }))}
          style={{ ...inputBase, width: "auto", flexShrink: 0 }}
        />
      </div>
      <input
        className="sm-input"
        type="text"
        aria-label="コメント"
        value={form.comment}
        maxLength={100}
        placeholder="コメント（任意）"
        onChange={(e) => setForm((f) => ({ ...f, comment: e.target.value }))}
        style={inputBase}
      />
      <div style={{ display: "flex", gap: "8px" }}>
        <button
          type="button"
          onClick={onCancel}
          style={{
            flex: 1, padding: "11px", borderRadius: "10px",
            border: "1.5px solid #e2e8f0", background: "#fff",
            color: "#64748b", cursor: "pointer", fontSize: "14px",
            fontWeight: "600",
            WebkitTapHighlightColor: "transparent",
          }}
        >
          キャンセル
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={loading}
          style={{
            flex: 2, padding: "11px", borderRadius: "10px", border: "none",
            background: loading
              ? "#a7f3d0"
              : "linear-gradient(135deg, #059669, #10b981)",
            color: "#fff", fontWeight: "700", fontSize: "14px",
            cursor: loading ? "not-allowed" : "pointer",
            boxShadow: loading ? "none" : "0 3px 10px rgba(16,185,129,0.35)",
            WebkitTapHighlightColor: "transparent",
          }}
        >
          {loading ? "更新中..." : "更新する"}
        </button>
      </div>
    </div>
  );
}

export default SightingEditForm;
